/**
 * Decode, resample, and encode PCM audio for TTS / voice cloning.
 */

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

export function downsample(input: Float32Array, inRate: number, outRate: number): Float32Array {
  if (inRate === outRate) return input;
  if (outRate > inRate) {
    const ratio = outRate / inRate;
    const newLen = Math.max(1, Math.floor(input.length * ratio));
    const out = new Float32Array(newLen);
    for (let i = 0; i < newLen; i++) {
      const src = i / ratio;
      const i0 = Math.floor(src);
      const i1 = Math.min(i0 + 1, input.length - 1);
      const t = src - i0;
      out[i] = input[i0] * (1 - t) + input[i1] * t;
    }
    return out;
  }
  const ratio = inRate / outRate;
  const newLen = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(newLen);
  for (let i = 0; i < newLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += input[j];
      count++;
    }
    out[i] = count ? sum / count : input[start] || 0;
  }
  return out;
}

export function toMono(buffer: any): Float32Array {
  const channels = buffer.numberOfChannels || 1;
  if (channels === 1) {
    return new Float32Array(buffer.getChannelData(0));
  }
  const len = buffer.length;
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    let sum = 0;
    for (let c = 0; c < channels; c++) {
      sum += buffer.getChannelData(c)[i];
    }
    out[i] = sum / channels;
  }
  return out;
}

/**
 * Decode File / Blob / ArrayBuffer → mono Float32 at targetRate.
 */
export async function decodeToMonoPcm(
  source: any,
  targetRate: number = 24000
): Promise<{ pcm: Float32Array; sampleRate: number }> {
  const win = getWin();
  const AC = win.AudioContext || win.webkitAudioContext;
  if (!AC) throw new Error('Trình duyệt không hỗ trợ Web Audio API.');

  let arrayBuf: ArrayBuffer;
  if (source instanceof ArrayBuffer) {
    arrayBuf = source;
  } else if (source && typeof source.arrayBuffer === 'function') {
    arrayBuf = await source.arrayBuffer();
  } else {
    throw new Error('Không đọc được file âm thanh.');
  }

  const ctx = new AC();
  let audioBuf: any;
  try {
    audioBuf = await ctx.decodeAudioData(arrayBuf.slice(0));
  } catch (e) {
    try {
      await ctx.close();
    } catch {
      /* ignore */
    }
    throw new Error('Không giải mã được file âm thanh. Hãy dùng MP3/WAV/OGG/M4A.');
  }
  try {
    await ctx.close();
  } catch {
    /* ignore */
  }

  const mono = toMono(audioBuf);
  const pcm = downsample(mono, audioBuf.sampleRate, targetRate);
  return { pcm, sampleRate: targetRate };
}

export function concatPcm(chunks: Float32Array[]): Float32Array {
  let total = 0;
  chunks.forEach((c) => {
    total += c.length;
  });
  const out = new Float32Array(total);
  let offset = 0;
  chunks.forEach((c) => {
    out.set(c, offset);
    offset += c.length;
  });
  return out;
}

export function encodeWav(pcm: Float32Array, sampleRate: number): any {
  const win = getWin();
  const BlobCtor = win.Blob || (global as any).Blob;
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < pcm.length; i++) {
    let s = Math.max(-1, Math.min(1, pcm[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new BlobCtor([buffer], { type: 'audio/wav' });
}

export function downloadBlob(blob: any, filename: string) {
  const win = getWin();
  const url = win.URL.createObjectURL(blob);
  const a = win.document.createElement('a');
  a.href = url;
  a.download = filename;
  win.document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    try {
      win.URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, 1500);
}

export function revokeUrl(url: string | null) {
  if (!url) return;
  try {
    const win = getWin();
    if (win.URL && win.URL.revokeObjectURL) win.URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

export function splitSentences(text: string): string[] {
  const cleaned = (text || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return [];
  // Avoid lookbehind for older browsers — split on punctuation then re-attach
  const out: string[] = [];
  let buf = '';
  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];
    buf += ch;
    if ('.!?…。！？'.indexOf(ch) >= 0) {
      const next = cleaned[i + 1];
      if (!next || next === ' ') {
        const t = buf.trim();
        if (t) out.push(t);
        buf = '';
      }
    }
  }
  const rest = buf.trim();
  if (rest) out.push(rest);
  return out.length ? out : [cleaned];
}

/**
 * Mic recorder → mono Float32 at targetRate (default 24 kHz for TTS clone).
 */
export type MicRecorder = {
  start: () => Promise<void>;
  stop: () => Promise<Float32Array>;
  isRecording: () => boolean;
};

export function createMicRecorder(maxSeconds: number = 30, targetRate: number = 24000): MicRecorder {
  const win = getWin();
  let stream: any = null;
  let audioCtx: any = null;
  let processor: any = null;
  let source: any = null;
  let chunks: Float32Array[] = [];
  let recording = false;
  let stopTimer: any = null;
  let sampleRate = targetRate;

  const cleanup = () => {
    if (stopTimer) {
      clearTimeout(stopTimer);
      stopTimer = null;
    }
    try {
      if (processor) {
        processor.onaudioprocess = null;
        processor.disconnect();
      }
    } catch {
      /* ignore */
    }
    try {
      if (source) source.disconnect();
    } catch {
      /* ignore */
    }
    try {
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
    } catch {
      /* ignore */
    }
    if (stream) {
      stream.getTracks().forEach((t: any) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
    }
    processor = null;
    source = null;
    audioCtx = null;
    stream = null;
    recording = false;
  };

  return {
    isRecording: () => recording,

    start: async () => {
      if (recording) return;
      chunks = [];
      const nav = win.navigator;
      if (!nav || !nav.mediaDevices || !nav.mediaDevices.getUserMedia) {
        throw new Error('Trình duyệt không hỗ trợ micro. Hãy dùng Chrome/Edge.');
      }
      try {
        stream = await nav.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
          },
        });
      } catch (err: any) {
        const name = err && err.name ? String(err.name) : '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          throw new Error('Bạn đã chặn quyền micro. Hãy mở cài đặt trang và cho phép micro.');
        }
        throw new Error((err && err.message) || 'Không mở được micro.');
      }

      const AC = win.AudioContext || win.webkitAudioContext;
      audioCtx = new AC();
      sampleRate = audioCtx.sampleRate || 48000;
      source = audioCtx.createMediaStreamSource(stream);
      const bufferSize = 4096;
      processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
      processor.onaudioprocess = (e: any) => {
        if (!recording) return;
        const input = e.inputBuffer.getChannelData(0);
        chunks.push(new Float32Array(input));
      };
      source.connect(processor);
      const mute = audioCtx.createGain();
      mute.gain.value = 0;
      processor.connect(mute);
      mute.connect(audioCtx.destination);
      recording = true;
      stopTimer = setTimeout(() => {
        recording = false;
      }, maxSeconds * 1000);
    },

    stop: async () => {
      const wasRecording = recording || chunks.length > 0;
      recording = false;
      if (!wasRecording && !chunks.length) {
        cleanup();
        return new Float32Array(0);
      }
      await new Promise((r) => setTimeout(r, 50));
      let total = 0;
      chunks.forEach((c) => {
        total += c.length;
      });
      const merged = new Float32Array(total);
      let offset = 0;
      chunks.forEach((c) => {
        merged.set(c, offset);
        offset += c.length;
      });
      cleanup();
      chunks = [];
      return downsample(merged, sampleRate, targetRate);
    },
  };
}
